import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { OptionType } from '@/api/common';
import { SelectPicker } from '@/components/Form/pickers';
import type { FilterOperatorMulti, FilterOperatorValue } from '@/components/InstantSearch';

import { IncludeExcludeToggle } from '../IncludeExcludeToggle';

type Props<T extends OptionType> = {
  value?: FilterOperatorMulti | null;
  options?: T[];
  isLoading?: boolean;
  error?: Error | null;
  hasExclude?: boolean;
  placeholder: string;
  notFoundContent: ReactNode;
  renderOption?: (option: T) => ReactNode;
  onChange?: (value: FilterOperatorValue) => void;
};

const SelectPickerFilter = <T extends OptionType>({
  value,
  options,
  isLoading = false,
  error,
  hasExclude = false,
  placeholder,
  notFoundContent,
  renderOption,
  onChange,
}: Props<T>) => {
  if (value?.operator && value.operator !== 'in' && value.operator !== 'notIn') {
    throw new Error(`Invalid operator: ${value.operator}`);
  }

  const operator = value?.operator ?? 'in';
  const optionsById = useMemo(
    () => new Map((options ?? []).map((option) => [option.id, option])),
    [options],
  );
  const selectedOption = useMemo<T | null>(() => {
    const selectedId = value?.operand?.[0];
    if (selectedId == null) return null;
    return optionsById.get(String(selectedId)) ?? null;
  }, [optionsById, value?.operand]);

  return (
    <div className="flex flex-col gap-2">
      {hasExclude && (
        <IncludeExcludeToggle
          operator={operator}
          onChange={(newOperator) => {
            onChange?.({
              operator: newOperator,
              operand: value?.operand ?? [],
            });
          }}
        />
      )}
      <SelectPicker<T>
        options={options}
        value={selectedOption}
        isLoading={isLoading}
        error={error}
        placeholder={placeholder}
        notFoundContent={notFoundContent}
        renderOption={renderOption}
        onChange={(selectedValue) => {
          if (!selectedValue) {
            onChange?.(null);
            return;
          }

          onChange?.({
            operator,
            operand: [selectedValue.id],
          });
        }}
      />
    </div>
  );
};

export default SelectPickerFilter;
